import React from "react";
import styled, { keyframes } from "styled-components";
import { FiLock, FiAlertTriangle } from "react-icons/fi";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 15px rgba(255, 75, 75, 0.2);
  }
  50% {
    box-shadow: 0 0 30px rgba(255, 75, 75, 0.45);
  }
  100% {
    box-shadow: 0 0 15px rgba(255, 75, 75, 0.2);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  background: radial-gradient(circle at center, #18181c 0%, #0d0d0f 100%);
  color: #f1f1f4;
  font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  overflow: hidden;
  padding: 20px;
  box-sizing: border-box;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 520px;
  width: 100%;
  padding: 48px 32px;
  background: rgba(22, 22, 26, 0.7);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 75, 75, 0.15);
  border-radius: 24px;
  animation: ${fadeIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) both, ${pulse} 3s infinite ease-in-out;
  box-sizing: border-box;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 75, 75, 0.1);
  border: 1px solid rgba(255, 75, 75, 0.25);
  color: #ff4b4b;
  margin-bottom: 28px;
  position: relative;
`;

const SmallIcon = styled.div`
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: #ff4b4b;
  color: #0d0d0f;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border: 2px solid #18181c;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.5px;
  margin: 0 0 16px 0;
  background: linear-gradient(135deg, #ffffff 0%, #a5a5b2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #ff4b4b;
  margin-bottom: 16px;
  letter-spacing: -0.2px;
`;

const Description = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #8f8f9e;
  margin: 0 0 32px 0;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #ff4b4b 0%, #d62e2e 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 14px rgba(255, 75, 75, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 75, 75, 0.45);
    background: linear-gradient(135deg, #ff5c5c 0%, #e53e3e 100%);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default function BlockedScreen({ message }) {
  const displayMessage = message || "This video is not authorized in this system. Access denied.";

  return (
    <Container>
      <Card>
        <IconWrapper>
          <FiLock size={36} />
          <SmallIcon>
            <FiAlertTriangle size={12} />
          </SmallIcon>
        </IconWrapper>
        <Title>Access Restricted</Title>
        <Subtitle>502 Bad Gateway</Subtitle>
        <Description>{displayMessage}</Description>
        <ActionButton onClick={() => window.location.reload()}>
          Retry Connection
        </ActionButton>
      </Card>
    </Container>
  );
}
